// @story HIR-HR5
// The offer's deadline and answer, on the candidate's report: when it must be
// answered by (and a way to move it), what the answer was, who recorded it and
// how — and, while it is open, a way for HR to record an answer the candidate
// gave them directly.
import React, { useState } from 'react';
import { offerLetterApi } from '../../../services/offerLetter.service';
import { formatWhen, timeLeft, isoDateInIndia, VIA_WORDS } from './offerDates';

const OUTCOME_LOOK = {
  accepted: ['text-emerald-700', 'Accepted'],
  declined: ['text-red-600', 'Declined'],
  rejected: ['text-red-600', 'Declined'],
  lapsed:   ['text-amber-700', 'Lapsed — no answer by the deadline'],
};

export default function OfferResponseRow({ resume, onChanged }) {
  const status = resume.offerLetterStatus;
  const o = resume.offerLetter || {};
  const [mode, setMode] = useState(null); // 'deadline' | 'accepted' | 'declined'
  const [date, setDate] = useState(o.deadline ? new Date(o.deadline).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }) : isoDateInIndia(7));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  if (!['sent', 'accepted', 'declined', 'lapsed', 'rejected'].includes(status)) return null;

  const run = async (fn) => {
    setBusy(true); setMsg('');
    try { await fn(); setMode(null); setNote(''); onChanged && onChanged(); } catch (e) { setMsg(e?.message || 'That did not work.'); } finally { setBusy(false); }
  };

  const r = o.response;
  const [tone, label] = OUTCOME_LOOK[status] || ['text-gray-600', 'Awaiting an answer'];

  return (
    <div className="mt-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-sm">
      {status === 'sent' ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-gray-500 font-medium">Reply by</span>
          {o.deadline
            ? <span className="font-semibold text-gray-800">{formatWhen(o.deadline)} <span className="font-normal text-gray-500">({timeLeft(o.deadline)})</span></span>
            : <span className="text-amber-700">No deadline set — set one so this offer cannot stay open for ever.</span>}
          {o.deadlineSetBy?.name && <span className="text-xs text-gray-400">set by {o.deadlineSetBy.name}{o.deadlineDefaulted ? ' (the usual 7 days)' : ''}</span>}
          <span className="flex gap-1.5 ml-auto">
            <button onClick={() => setMode(mode === 'deadline' ? null : 'deadline')} className="text-xs px-2 py-1 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-100">Change date</button>
            <button onClick={() => setMode(mode === 'accepted' ? null : 'accepted')} className="text-xs px-2 py-1 rounded-lg border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50">Record accepted</button>
            <button onClick={() => setMode(mode === 'declined' ? null : 'declined')} className="text-xs px-2 py-1 rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50">Record declined</button>
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-gray-500 font-medium">Answer</span>
          <span className={`font-semibold ${tone}`}>{label}</span>
          {r?.at && <span className="text-xs text-gray-500">{formatWhen(r.at)}</span>}
          {r?.via && (
            <span className="text-xs text-gray-500">
              {r.via === 'hr_on_behalf' && r.by?.name ? `recorded by ${r.by.name} ${VIA_WORDS.hr_on_behalf}` : VIA_WORDS[r.via]}
            </span>
          )}
          {r?.note && <span className="text-xs text-gray-500 italic">“{r.note}”</span>}
        </div>
      )}

      {mode === 'deadline' && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input type="date" value={date} min={isoDateInIndia(1)} max={isoDateInIndia(90)} onChange={(e) => setDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm" />
          <span className="text-xs text-gray-500">until the end of that day</span>
          <button disabled={busy} onClick={() => run(() => offerLetterApi.updateOfferDeadline(resume._id, date))}
            className="text-xs px-3 py-1.5 rounded-lg bg-orange-500 text-white font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Save date'}</button>
        </div>
      )}

      {(mode === 'accepted' || mode === 'declined') && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-gray-600">
            {mode === 'accepted'
              ? 'Record that the candidate told you they accept. It will show as recorded by you, on their behalf.'
              : 'Record that the candidate told you they decline. It will show as recorded by you, on their behalf, and the next candidates for this role will be put in front of you.'}
          </p>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="How they told you (optional) — e.g. phoned on 6 Oct"
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
          <button disabled={busy} onClick={() => run(() => offerLetterApi.updateOfferStatus(resume._id, mode, note))}
            className={`text-xs px-3 py-1.5 rounded-lg text-white font-semibold disabled:opacity-50 ${mode === 'accepted' ? 'bg-emerald-600' : 'bg-red-600'}`}>
            {busy ? 'Saving…' : mode === 'accepted' ? 'Record accepted' : 'Record declined'}
          </button>
        </div>
      )}
      {msg && <p className="text-xs text-red-600 mt-1">{msg}</p>}

      {Array.isArray(o.history) && o.history.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-gray-500 cursor-pointer">History ({o.history.length})</summary>
          <ul className="mt-1 space-y-0.5">
            {o.history.map((h, i) => (
              <li key={i} className="text-xs text-gray-600">
                <span className="text-gray-400">{formatWhen(h.at)}</span> — {h.detail || h.event}{h.by?.name && h.via !== 'candidate_link' ? ` · ${h.by.name}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
